/**
 * AW RM scroll-triggered animations.
 *
 * Three effects, all driven by GSAP + ScrollTrigger (enqueued globally):
 *
 *   1. `.aw-rm-5050` rows — the two columns slide in toward each other
 *      (image from the left, text from the right) and fade in.
 *
 *   2. `.aw-rm-benefits-table` — body rows stagger in from a small y offset.
 *
 *   3. Bordered cards (`.aw-rm-stat`, `.aw-rm-resource-card`,
 *      `.aw-rm-info-card`, `.aw-rm-contact-card`, `.block-accordion
 *      .accordion-item`) — the rounded border is drawn around the card via
 *      an SVG overlay with animated `stroke-dashoffset`, then the original
 *      CSS border is restored so hover/focus states keep working.
 *
 * Honors `prefers-reduced-motion: reduce`. FacetWP load-more (resources
 * grid) re-initializes new cards via the `facetwp-loaded` event.
 */

( function () {
	'use strict';

	function ready( fn ) {
		if ( document.readyState !== 'loading' ) {
			fn();
		} else {
			document.addEventListener( 'DOMContentLoaded', fn );
		}
	}

	// ------------------------------------------------------------------
	// Anchor-click handler — runs regardless of reduced-motion.
	//
	// First-click-misses bug: when the "Jump to FAQs" link is clicked, the
	// native anchor scroll computes the target position immediately, but
	// ScrollTrigger's pending refresh (or images settling) shift the FAQ
	// section a few pixels right after, so the browser lands in blank
	// space. Second click works because the layout has stabilised.
	//
	// Fix: intercept any `<a href="#…">` click, call ScrollTrigger.refresh()
	// to settle trigger positions, then wait one frame and scroll using the
	// freshly-measured target rect.
	// ------------------------------------------------------------------
	ready( function () {
		document.addEventListener( 'click', function ( e ) {
			var link = e.target.closest && e.target.closest( 'a[href^="#"]' );
			if ( ! link ) {
				return;
			}
			var hash = link.getAttribute( 'href' );
			if ( ! hash || hash.length < 2 || hash === '#' ) {
				return;
			}
			var target;
			try {
				target = document.querySelector( hash );
			} catch ( err ) {
				return;
			}
			if ( ! target ) {
				return;
			}
			e.preventDefault();

			if ( window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function' ) {
				window.ScrollTrigger.refresh();
			}

			window.requestAnimationFrame( function () {
				// 50px breathing room above the target so the heading isn't
				// flush against the viewport top.
				var ANCHOR_OFFSET = 50;
				var top = target.getBoundingClientRect().top + window.pageYOffset - ANCHOR_OFFSET;
				window.scrollTo( {
					top: top,
					behavior: 'smooth'
				} );
				if ( history && history.pushState ) {
					history.pushState( null, '', hash );
				}
			} );
		} );
	} );

	ready( function () {
		if ( typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined' ) {
			return;
		}

		// Respect reduced-motion preference.
		if ( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
			return;
		}

		var gsap = window.gsap;
		var ScrollTrigger = window.ScrollTrigger;
		gsap.registerPlugin( ScrollTrigger );

		// --------------------------------------------------------------
		// 1. 50/50 columns slide in toward each other
		// --------------------------------------------------------------
		document.querySelectorAll( '.aw-rm-5050' ).forEach( function ( row ) {
			var cols = row.querySelectorAll( ':scope > .wp-block-column' );
			if ( cols.length < 2 ) {
				return;
			}
			gsap.fromTo(
				cols[ 0 ],
				{ x: -80, autoAlpha: 0 },
				{
					x: 0,
					autoAlpha: 1,
					duration: 0.9,
					ease: 'power2.out',
					scrollTrigger: { trigger: row, start: 'top 80%', once: true }
				}
			);
			gsap.fromTo(
				cols[ cols.length - 1 ],
				{ x: 80, autoAlpha: 0 },
				{
					x: 0,
					autoAlpha: 1,
					duration: 0.9,
					ease: 'power2.out',
					scrollTrigger: { trigger: row, start: 'top 80%', once: true }
				}
			);
		} );

		// --------------------------------------------------------------
		// 2. Stagger: benefits table rows + benefits grid cells
		// --------------------------------------------------------------
		document.querySelectorAll( '.aw-rm-benefits-table' ).forEach( function ( table ) {
			var rows = table.querySelectorAll( 'tbody tr' );
			if ( ! rows.length ) {
				return;
			}
			gsap.fromTo(
				rows,
				{ y: 24, autoAlpha: 0 },
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.45,
					ease: 'power2.out',
					stagger: 0.08,
					scrollTrigger: { trigger: table, start: 'top 80%', once: true }
				}
			);
		} );

		// Home page "Why partner with American Water" 2x3 grid — stagger the
		// cells in the same rhythm as the benefits table.
		document.querySelectorAll( '.aw-rm-benefits-grid' ).forEach( function ( grid ) {
			var cells = grid.querySelectorAll( '.aw-rm-benefits-grid__cell' );
			if ( ! cells.length ) {
				return;
			}
			gsap.fromTo(
				cells,
				{ y: 24, autoAlpha: 0 },
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.45,
					ease: 'power2.out',
					stagger: 0.08,
					scrollTrigger: { trigger: grid, start: 'top 80%', once: true }
				}
			);
		} );

		// --------------------------------------------------------------
		// 3. Bordered-card draw-in
		// --------------------------------------------------------------
		var cardSelector = [
			'.aw-rm-stat',
			'.aw-rm-resource-card',
			'.aw-rm-info-card',
			'.aw-rm-news-card',
			'.block-accordion .accordion-item'
		].join( ',' );

		function initBorderDraw( card ) {
			if ( card.dataset.borderInit === '1' ) {
				return;
			}
			card.dataset.borderInit = '1';

			var cs = window.getComputedStyle( card );
			var stroke = cs.borderTopColor;
			var strokeWidth = parseFloat( cs.borderTopWidth ) || 0;
			var radius = parseFloat( cs.borderTopLeftRadius ) || 0;
			if ( ! strokeWidth || ! stroke || stroke === 'rgba(0, 0, 0, 0)' || stroke === 'transparent' ) {
				return;
			}

			// Make the card a positioning context if it isn't already, and
			// hide its static border (+ overflow) for the duration of the
			// draw — the CSS class is removed when the animation completes
			// so hover/focus colour swaps still work afterward.
			card.classList.add( 'aw-rm-card-drawing' );
			if ( cs.position === 'static' ) {
				card.style.position = 'relative';
			}

			var svgNS = 'http://www.w3.org/2000/svg';
			var svg = document.createElementNS( svgNS, 'svg' );
			svg.setAttribute( 'class', 'aw-rm-border-draw-svg' );
			svg.setAttribute( 'aria-hidden', 'true' );
			Object.assign( svg.style, {
				position: 'absolute',
				top: '-' + strokeWidth + 'px',
				left: '-' + strokeWidth + 'px',
				pointerEvents: 'none',
				overflow: 'visible',
				zIndex: '2'
			} );

			var rect = document.createElementNS( svgNS, 'rect' );
			rect.setAttribute( 'fill', 'none' );
			rect.setAttribute( 'stroke', stroke );
			rect.setAttribute( 'stroke-width', strokeWidth );
			svg.appendChild( rect );
			card.appendChild( svg );

			var drawn = false;

			function sizeRect() {
				var w = card.offsetWidth + ( 2 * strokeWidth );
				var h = card.offsetHeight + ( 2 * strokeWidth );
				svg.setAttribute( 'width', w );
				svg.setAttribute( 'height', h );

				var innerW = Math.max( 0, w - strokeWidth );
				var innerH = Math.max( 0, h - strokeWidth );
				rect.setAttribute( 'x', strokeWidth / 2 );
				rect.setAttribute( 'y', strokeWidth / 2 );
				rect.setAttribute( 'width', innerW );
				rect.setAttribute( 'height', innerH );

				var r = Math.max( 0, Math.min( radius, innerW / 2, innerH / 2 ) );
				rect.setAttribute( 'rx', r );
				rect.setAttribute( 'ry', r );

				var perimeter = ( 2 * ( innerW + innerH ) ) - ( 8 * r ) + ( 2 * Math.PI * r );
				rect.style.strokeDasharray = perimeter;
				rect.style.strokeDashoffset = drawn ? 0 : perimeter;
			}
			sizeRect();

			if ( typeof window.ResizeObserver !== 'undefined' ) {
				new window.ResizeObserver( sizeRect ).observe( card );
			}

			gsap.to( rect, {
				strokeDashoffset: 0,
				duration: 1.0,
				ease: 'power2.out',
				scrollTrigger: { trigger: card, start: 'top 85%', once: true },
				onComplete: function () {
					drawn = true;
					card.classList.remove( 'aw-rm-card-drawing' );
					// Defer removal one frame so the restored CSS border has
					// painted before the SVG comes off (prevents flicker).
					window.requestAnimationFrame( function () {
						if ( svg.parentNode ) {
							svg.parentNode.removeChild( svg );
						}
					} );
				}
			} );
		}

		function initAllBorderDraws( root ) {
			( root || document ).querySelectorAll( cardSelector ).forEach( initBorderDraw );
		}
		initAllBorderDraws();

		// FacetWP load-more (resources grid) brings in new cards.
		document.addEventListener( 'facetwp-loaded', function () {
			initAllBorderDraws();
		} );
	} );
}() );
